import React from 'react';
import { useModal } from '@europrocurement/flexy-components';
import type {
    Collection,
    CollectionInvoice,
} from '../../../../../../providers/CollectionBillingProvider';
import ModalPaiement from '../../ModalPaiement/ModalPaiement';
import { useBillingRender, useExportDossier } from '../../../../hooks';
import useBilling from '../../../../hooks/useBilling';
import { useModalMail } from '../../../../../MailForm/hooks';
import { usePanierPay } from '../../../../../Paiement/hooks';

const usePayActions = () => {
    const { modalActions } = useModal();
    const { bodyMailFacture, bodyMailCollection } = usePanierPay();
    const { showMailModal } = useModalMail();
    const { searchContacts } = useExportDossier();
    const { downloadInvoice } = useBilling({ origine: 'B2D' });
    const { getInvoiceLabel } = useBillingRender();

    const handlePayInvoices = (
        collectionNumero: string,
        amount: number,
        ids: Array<number>,
        type: 'facture' | 'collection',
    ) => {
        modalActions.call(
            <ModalPaiement
                ids={ids}
                amount={amount}
                type={type}
                collectionNumero={collectionNumero}
                chequePayActif={false}
                virementPayActif={false}
                typoBackground={false}
            />,
        );
    };

    const handleSendInvoicePaymentMail = async (
        recipientId: number,
        prescripteurId: number,
        collections: Collection[],
        resteAPayer: string,
        invoices: CollectionInvoice[],
        dossierIds?: number[],
    ): Promise<void> => {
        if (!recipientId) throw new Error('Aucun client sélectionné');
        if (!prescripteurId) throw new Error('Aucun prescripteur sélectionné');
        if (!collections) throw new Error('Aucune commande sélectionnée');

        const billingType =
            collections[0]?.venteIndirecte !== undefined ? collections[0].venteIndirecte : true;

        // Dans le cas d'un dossier NOR ou DOD on sélectionne par défaut le contact compta du prescripteur.
        // Dans le cas d'un dossier DOI on sélectionne le contact du client facturé.
        const tiersAContacter = billingType ? recipientId : prescripteurId;
        const contact = await searchContacts('', [tiersAContacter], true);

        if (!contact || contact.length === 0) {
            throw new Error('Aucun contact trouvé pour le client sélectionné');
        }

        const builtAttachments = await Promise.all(
            invoices.map(async (invoice: CollectionInvoice) => {
                const blob = await downloadInvoice(invoice.id);
                const filename = invoice ? `${getInvoiceLabel(invoice)}.pdf` : `facture.pdf`;

                return {
                    file: new File([blob], filename, { type: 'application/pdf' }),
                };
            }),
        );

        const body: string = await bodyMailFacture(
            `${contact[0].firstName} ${contact[0].lastName}`,
            collections[0]?.numero,
            resteAPayer || '0',
            invoices.map((invoice) => invoice.id),
        );
        showMailModal({
            to: [
                {
                    emailAddress: contact[0].emailAddress,
                    firstName: contact[0].firstName,
                    lastName: contact[0].lastName,
                },
            ],
            subject: `Demande de règlement`,
            showSubject: true,
            body,
            showTo: true,
            searchParamsTiersIds: [prescripteurId as number, recipientId],
            showBody: true,
            showAttachments: true,
            dossiersIds: dossierIds || [],
            collectionIds: collections.map((c) => c.id),
            attachments: [...builtAttachments],
            syncGreeting: true,
            showSignature: true,
        });

        return Promise.resolve();
    };

    const handleSendAcomptePaymentMail = async (
        recipientId: number,
        prescripteurId: number,
        collections: Collection[],
        totalAcomptes: number,
        dossierIds?: number[],
    ): Promise<void> => {
        if (!recipientId) throw new Error('Aucun client sélectionné');
        if (!prescripteurId) throw new Error('Aucun prescripteur sélectionné');
        if (!collections) throw new Error('Aucune commande sélectionnée');

        const mainCollection = collections[0] || undefined;

        if (!mainCollection) {
            return Promise.reject();
        }

        const billingType =
            mainCollection?.venteIndirecte !== undefined ? mainCollection.venteIndirecte : true;

        // Dans le cas d'un dossier NOR ou DOD on sélectionne par défaut le contact compta du prescripteur.
        // Dans le cas d'un dossier DOI on sélectionne le contact du client facturé.
        const tiersAContacter = billingType ? recipientId : prescripteurId;
        const contact = await searchContacts('', [tiersAContacter], true);

        if (!contact || contact.length === 0) {
            throw new Error('Aucun contact trouvé pour le client sélectionné');
        }

        const amount = mainCollection.acompteAttendu as number;

        const body: string | undefined = await bodyMailCollection(
            `${contact[0].firstName} ${contact[0].lastName}`,
            mainCollection.numero,
            mainCollection.id,
            Math.min(Math.max(amount - totalAcomptes, 0), amount),
        );

        showMailModal({
            to: [
                {
                    emailAddress: contact[0].emailAddress,
                    firstName: contact[0].firstName,
                    lastName: contact[0].lastName,
                },
            ],
            subject: `Demande de provision`,
            showSubject: true,
            body,
            showTo: true,
            searchParamsTiersIds: [prescripteurId as number, recipientId],
            showBody: true,
            showAttachments: true,
            syncGreeting: true,
            showSignature: true,
            dossiersIds: dossierIds || [],
            collectionIds: collections.map((c) => c.id),
            showDriveDocButton: true,
        });

        return Promise.resolve();
    };

    return { handlePayInvoices, handleSendInvoicePaymentMail, handleSendAcomptePaymentMail };
};

export default usePayActions;
